import { useState, useCallback, forwardRef } from 'react';
import classNames from 'classnames';
import { Icon } from '../icon';
import styles from './counter-input.module.scss';

export interface CounterInputProps {
  value?: number;
  onChange?: (value: number) => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  readonly?: boolean;
  size?: 'small' | 'normal';
  hasError?: boolean;
  className?: string;
}

export const CounterInput = forwardRef<HTMLDivElement, CounterInputProps>(({
  value = 0,
  onChange,
  onBlur,
  min,
  max,
  disabled = false,
  readonly = false,
  size = 'normal',
  hasError = false,
  className
}, ref) => {
  const [focused, setFocused] = useState(false);

  const isBelowMax = max != null ? value < max : true;
  const isAboveMin = min != null ? value > min : true;

  const decrease = useCallback(() => {
    if (isAboveMin && !disabled && !readonly) {
      onChange?.(value - 1);
    }
  }, [isAboveMin, disabled, readonly, onChange, value]);

  const increase = useCallback(() => {
    if (isBelowMax && !disabled && !readonly) {
      onChange?.(value + 1);
    }
  }, [isBelowMax, disabled, readonly, onChange, value]);

  return (
    <div className={classNames(styles.host, className)} ref={ref}>
      <div 
        className={classNames(styles.counterInput, 'input', {
          [styles.isSmall]: size === 'small',
          [styles.isFocused]: focused,
          [styles.isDanger]: hasError,
          'readonly': readonly
        })}
        aria-disabled={disabled}
      >
        <button
          type="button"
          className={classNames(styles.btn, 'button', { 'is-small': size === 'small' })}
          disabled={disabled || !isAboveMin}
          onClick={decrease}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          data-e2e="decrease count button"
        >
          <Icon name="minus-small" />
        </button>
        <div className={styles.quantity} tabIndex={-1}>
          {value}
        </div>
        <button
          type="button"
          className={classNames(styles.btn, 'button', { 'is-small': size === 'small' })}
          disabled={disabled || !isBelowMax}
          onClick={increase}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          data-e2e="increase count button"
        >
          <Icon name="plus-small" />
        </button>
      </div>
    </div>
  );
});

CounterInput.displayName = 'CounterInput';
